import React, { memo } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

import { useTranslation } from 'react-i18next';

import { propTypesNullable } from '@functional';
import { QUALIFICATION_GENDER_TYPE, QUOTA_GENDER_TYPE, LOCALE_STRING } from '~/constants';
import { FONT_WEIGHTS, FONT_SIZES, PALETTE } from '~/assets/styles/base';

const NoDataWrapper = styled.div`
  width: 100%;
  padding: 20px 0;
  background: ${PALETTE.bg.main};
  text-align: center;
`;

const StyledTableHeadRow = styled(TableRow)`
  background: ${PALETTE.white};
`;

const StyledTableCell = styled(TableCell)`
  text-align: center;
  background: ${(props) => (props.$bgColored ? PALETTE.primary.superLight : 'inherit')};
  border: 1px solid ${PALETTE.border.table};
  font-size: ${FONT_SIZES.bodyDefault};
  color: ${(props) => (props.$colored ? PALETTE.text.primary : PALETTE.text.main)};
`;

const TotalTableCell = styled(StyledTableCell)`
  ${(props) =>
    props.$colored &&
    css`
      background: ${() => (props.$index % 2 === 0 ? PALETTE.bg.main : '#F4F8FC')};
    `}

  ${(props) =>
    props.$bgStrong &&
    css`
      background: #e3f0fd;
      font-weight: ${FONT_WEIGHTS.size1};
    `}

  ${(props) =>
    !props.$colored &&
    !props.$bgStrong &&
    css`
      background: ${PALETTE.white};
    `}
`;

const StyledTableBodyRow = styled(TableRow)`
  background: ${(props) => (props.$index % 2 === 0 ? PALETTE.bg.main : '#F4F8FC')};
`;

function QuotaListTableWithCollectCounts({
  ages,
  enqueteCollectCounts,
  targetSamples,
  qualificationsGender,
  minWidth = 700,
  isAdmin = false,
}) {
  const { t } = useTranslation();

  /**
   * enqueteCollectCountsから年齢性別条件（key）に合致する回答数を抽出して返す。
   */
  const getCollectCountWithoutLocaleString = (key) => {
    if (!enqueteCollectCounts[key]) {
      return 0;
    }
    return Number(enqueteCollectCounts[key]);
  };

  const getCollectCount = (key) => {
    return getCollectCountWithoutLocaleString(key).toLocaleString(LOCALE_STRING.ja);
  };

  const getCollectCountSum = (keyMale, keyFemale) => {
    const maleRes = getCollectCountWithoutLocaleString(keyMale);
    const femaleRes = getCollectCountWithoutLocaleString(keyFemale);
    const res = maleRes + femaleRes;
    return res.toLocaleString(LOCALE_STRING.ja);
  };

  const getCollectCountSumOfGender = (genderKey) => {
    return ages
      .reduce((prev, age) => {
        return getCollectCountWithoutLocaleString(`${age}-${genderKey}`) + prev;
      }, 0)
      .toLocaleString(LOCALE_STRING.ja);
  };

  const getCollectCountTotal = () => {
    return ages
      .reduce((prev, age) => {
        return (
          getCollectCountWithoutLocaleString(`${age}-${QUOTA_GENDER_TYPE.MALE}`) +
          getCollectCountWithoutLocaleString(`${age}-${QUOTA_GENDER_TYPE.FEMALE}`) +
          prev
        );
      }, 0)
      .toLocaleString(LOCALE_STRING.ja);
  };

  const getTargetSample = (key) => {
    return targetSamples[key] === undefined
      ? 0
      : targetSamples[key].toLocaleString(LOCALE_STRING.ja);
  };

  // agesが空、もしくはqualificationsGenderがnullならNoData用の表示。要件的にはあり得ないが念のため。
  if (!ages || ages.length === 0 || qualificationsGender === null) {
    return !isAdmin ? <NoDataWrapper>{t('quotas.table.noData')}</NoDataWrapper> : null;
  }

  return (
    <TableContainer component="div" data-testid="QuotaListTableWithCollectCounts">
      <Table sx={{ minWidth }} aria-label="customized table">
        {qualificationsGender === QUALIFICATION_GENDER_TYPE.EACH ? (
          <TableHead>
            <StyledTableHeadRow>
              {/* 2 rows cell */}
              <StyledTableCell rowSpan={2} sx={{ width: '185px' }}>
                {t('quotas.table.header.age')}
              </StyledTableCell>

              {/* 1 row cell */}
              <StyledTableCell colSpan={2} $bgColored data-testid="MaleHeaderCell">
                {t('quotas.table.header.male')}
              </StyledTableCell>

              <StyledTableCell colSpan={2} $bgColored data-testid="FemaleHeaderCell">
                {t('quotas.table.header.female')}
              </StyledTableCell>

              {/* 2 rows cell */}
              <StyledTableCell rowSpan={2} sx={{ width: '130px' }}>
                {t('quotas.table.header.enqueteCollectCountsSum')}
              </StyledTableCell>
            </StyledTableHeadRow>

            <StyledTableHeadRow>
              <StyledTableCell sx={{ width: '150px' }}>
                {t('quotas.table.header.targetSamples')}
              </StyledTableCell>

              <StyledTableCell sx={{ width: '150px' }}>
                {t('quotas.table.header.enqueteCollectCounts')}
              </StyledTableCell>

              <StyledTableCell sx={{ width: '150px' }}>
                {t('quotas.table.header.targetSamples')}
              </StyledTableCell>

              <StyledTableCell sx={{ width: '150px' }}>
                {t('quotas.table.header.enqueteCollectCounts')}
              </StyledTableCell>
            </StyledTableHeadRow>
          </TableHead>
        ) : (
          <TableHead>
            <StyledTableHeadRow>
              {/* 2 rows cell */}
              <StyledTableCell rowSpan={2} sx={{ width: '30%' }}>
                {t('quotas.table.header.age')}
              </StyledTableCell>

              {/* 1 row cell */}
              {qualificationsGender === QUALIFICATION_GENDER_TYPE.MALE && (
                <StyledTableCell colSpan={2} $bgColored data-testid="MaleHeaderCell">
                  {t('quotas.table.header.male')}
                </StyledTableCell>
              )}

              {qualificationsGender === QUALIFICATION_GENDER_TYPE.FEMALE && (
                <StyledTableCell colSpan={2} $bgColored data-testid="FemaleHeaderCell">
                  {t('quotas.table.header.female')}
                </StyledTableCell>
              )}

              {qualificationsGender === QUALIFICATION_GENDER_TYPE.BOTH && (
                <StyledTableCell colSpan={2} $bgColored data-testid="BothHeaderCell">
                  {t('quotas.table.header.both')}
                </StyledTableCell>
              )}
            </StyledTableHeadRow>

            <StyledTableHeadRow>
              <StyledTableCell sx={{ width: '35%' }}>
                {t('quotas.table.header.targetSamples')}
              </StyledTableCell>
              <StyledTableCell sx={{ width: '35%' }}>
                {t('quotas.table.header.enqueteCollectCounts')}
              </StyledTableCell>
            </StyledTableHeadRow>
          </TableHead>
        )}
        <TableBody>
          {qualificationsGender === QUALIFICATION_GENDER_TYPE.EACH
            ? // 男女両方の場合
              ages.map((age, i) => (
                <StyledTableBodyRow
                  key={age}
                  $index={i}
                  data-testid={`QuotaListTableWithCollectCountsRow-${i}`}
                >
                  <StyledTableCell component="th" scope="row">
                    {age}
                  </StyledTableCell>

                  <StyledTableCell>
                    {getTargetSample(`${age}-${QUOTA_GENDER_TYPE.MALE}`)}
                  </StyledTableCell>

                  <StyledTableCell $colored>
                    {getCollectCount(`${age}-${QUOTA_GENDER_TYPE.MALE}`)}
                  </StyledTableCell>

                  <StyledTableCell>
                    {getTargetSample(`${age}-${QUOTA_GENDER_TYPE.FEMALE}`)}
                  </StyledTableCell>

                  <StyledTableCell $colored>
                    {getCollectCount(`${age}-${QUOTA_GENDER_TYPE.FEMALE}`)}
                  </StyledTableCell>

                  <StyledTableCell $colored>
                    {getCollectCountSum(
                      `${age}-${QUOTA_GENDER_TYPE.MALE}`,
                      `${age}-${QUOTA_GENDER_TYPE.FEMALE}`
                    )}
                  </StyledTableCell>
                </StyledTableBodyRow>
              ))
            : // それ以外の場合
              ages.map((age, i) => (
                <StyledTableBodyRow
                  key={age}
                  $index={i}
                  data-testid={`QuotaListTableWithCollectCountsRow-${i}`}
                >
                  <StyledTableCell component="th" scope="row">
                    {age}
                  </StyledTableCell>

                  <StyledTableCell>
                    {getTargetSample(`${age}-${qualificationsGender}`)}
                  </StyledTableCell>

                  <StyledTableCell $colored>
                    {getCollectCount(`${age}-${qualificationsGender}`)}
                  </StyledTableCell>
                </StyledTableBodyRow>
              ))}
          {qualificationsGender === QUALIFICATION_GENDER_TYPE.EACH && (
            <TableRow data-testid="QuotaListTableWithCollectCountsRow-total">
              <TotalTableCell component="th" scope="row">
                {t('quotas.table.header.enqueteCollectCountsSum')}
              </TotalTableCell>

              <TotalTableCell />

              <TotalTableCell $colored $index={ages.length}>
                {getCollectCountSumOfGender(QUOTA_GENDER_TYPE.MALE)}
              </TotalTableCell>

              <TotalTableCell />

              <TotalTableCell $colored $index={ages.length}>
                {getCollectCountSumOfGender(QUOTA_GENDER_TYPE.FEMALE)}
              </TotalTableCell>

              <TotalTableCell $colored $bgStrong>
                {getCollectCountTotal()}
              </TotalTableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

QuotaListTableWithCollectCounts.propTypes = {
  ages: PropTypes.arrayOf(PropTypes.string).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  enqueteCollectCounts: PropTypes.object.isRequired,
  targetSamples: PropTypes.shape({ targetSamples: PropTypes.number }).isRequired,
  qualificationsGender: propTypesNullable(PropTypes.number.isRequired),
  errors: PropTypes.shape({
    quotas: PropTypes.shape({
      message: PropTypes.string,
      invalid: PropTypes.bool,
    }),
    targetSamples: PropTypes.shape({
      message: PropTypes.string,
      invalid: PropTypes.bool,
    }),
  }),
  minWidth: PropTypes.number,
  isAdmin: PropTypes.bool,
};

export default memo(QuotaListTableWithCollectCounts);
